import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type PointerEvent,
} from "react";
import { UNSAFE_PortalProvider } from "react-aria";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  toggleAudioPlayback,
  useAudioIsPlaying,
} from "../../components/audioPlayer/audioPlayback.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { AudioPlayerContext } from "../../components/audioPlayer/context.js";
import { useAutoHide } from "../../hooks/useAutoHide.js";

interface VideoOverlayProps {
  isFullscreen: boolean;
  isFrameLoading: boolean;
  onFullscreenToggle: () => void;
}

// Inline activity and full-screen edge activity use the short delay; pointer
// activity elsewhere in full screen uses the default delay.
const DEFAULT_HIDE_DELAY_MS = 3500;
const SHORT_HIDE_DELAY_MS = 2000;
const SCREEN_EDGE_PX = 20;

/** Controls shared by the embedded and fullscreen video presentations. */
export const VideoOverlay: FC<VideoOverlayProps> = (props) => {
  const { isFullscreen, isFrameLoading, onFullscreenToggle } = props;
  const audioPlayer = useContext(AudioPlayerContext);
  const nativeAudio = audioPlayer.nativeAudio;
  const isPlaying = useAudioIsPlaying(nativeAudio);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [hasOpenPlayerPopover, setHasOpenPlayerPopover] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);
  const fullscreenToggleRef = useRef<HTMLButtonElement>(null);
  const wasFullscreenRef = useRef(isFullscreen);
  const { isVisible: wasRecentlyActive, show: showTemporarily } = useAutoHide();
  useEffect(() => {
    showTemporarily(isFullscreen ? DEFAULT_HIDE_DELAY_MS : SHORT_HIDE_DELAY_MS);
  }, [isFullscreen, showTemporarily]);
  const handlePointerActivity = (event: PointerEvent<HTMLDivElement>) => {
    if (
      event.target instanceof Node &&
      playerRef.current?.contains(event.target)
    ) {
      showTemporarily(DEFAULT_HIDE_DELAY_MS);
      return;
    }
    if (!isFullscreen) {
      showTemporarily(SHORT_HIDE_DELAY_MS);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const edgeDistance = Math.min(
      event.clientX - rect.left,
      rect.right - event.clientX,
      event.clientY - rect.top,
      rect.bottom - event.clientY,
    );
    showTemporarily(
      edgeDistance < SCREEN_EDGE_PX
        ? SHORT_HIDE_DELAY_MS
        : DEFAULT_HIDE_DELAY_MS,
    );
  };
  useLayoutEffect(() => {
    if (wasFullscreenRef.current && !isFullscreen) {
      // The overlay player unmounts with any menu it had open.
      setHasOpenPlayerPopover(false);
      fullscreenToggleRef.current?.focus();
    }
    wasFullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  const areControlsVisible =
    hasFocusWithin ||
    hasOpenPlayerPopover ||
    (isFrameLoading
      ? isHovered
      : isFullscreen || isPlaying
        ? wasRecentlyActive
        : isHovered);
  const isOverlayVisible = isFrameLoading || areControlsVisible;
  const isPlaybackIconVisible = !isFrameLoading && areControlsVisible;
  return (
    <div
      className={twMerge(
        "absolute inset-0 z-10 transition-opacity duration-150",
        !isOverlayVisible && "opacity-0",
      )}
      onPointerEnter={() => {
        setIsHovered(true);
      }}
      onPointerLeave={() => setIsHovered(false)}
      onPointerMove={handlePointerActivity}
      onPointerDown={handlePointerActivity}
      onFocus={(event) =>
        setHasFocusWithin(event.target.matches(":focus-visible"))
      }
      onBlur={(event) => {
        if (
          !(event.relatedTarget instanceof Node) ||
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          setHasFocusWithin(false);
        }
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-black/25"
      />

      <AriaButton
        aria-label={isPlaying ? "Pause video" : "Play video"}
        isDisabled={!nativeAudio || audioPlayer.currentSourceKey === undefined}
        onPress={() => toggleAudioPlayback(nativeAudio)}
        className={twMerge(
          "absolute inset-0 z-10 grid place-items-center text-white outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset disabled:cursor-not-allowed",
          isFullscreen && !areControlsVisible && "cursor-none",
        )}
      >
        <span className="relative size-6">
          <span
            role="status"
            aria-hidden={!isFrameLoading}
            className={twMerge(
              "absolute inset-0 flex scale-100 items-center justify-center opacity-100 transition-[opacity,scale] duration-200 ease-out motion-reduce:transition-none",
              !isFrameLoading && "scale-[0.96] opacity-0",
            )}
          >
            <Loader
              style={LoaderStyle.SPINNER}
              className="size-6 fill-white text-white/30"
            />
            {isFrameLoading ? (
              <span className="sr-only">Loading frame...</span>
            ) : null}
          </span>
          <span
            aria-hidden
            className={twMerge(
              "absolute inset-0 flex scale-100 items-center justify-center opacity-100 transition-[opacity,scale] duration-200 ease-out motion-reduce:transition-none",
              !isPlaybackIconVisible && "scale-[0.96] opacity-0",
            )}
          >
            {isPlaying ? (
              <IconPlayerPause className="size-6" />
            ) : (
              <IconPlayerPlay className="size-6 translate-x-px" />
            )}
          </span>
        </span>
      </AriaButton>

      <div
        className={twMerge(
          "absolute inset-x-0 bottom-0 z-20 flex items-center px-2 pb-2 transition-[opacity,translate] duration-200 ease-out motion-reduce:transition-none",
          isFullscreen && "px-4",
          !areControlsVisible && "pointer-events-none translate-y-2 opacity-0",
        )}
      >
        {isFullscreen ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
            style={{
              backgroundImage: `linear-gradient(
                to top,
                color-mix(in srgb, var(--color-gray-950) 90%, transparent) 0%,
                color-mix(in srgb, var(--color-gray-950) 78%, transparent) 18%,
                color-mix(in srgb, var(--color-gray-950) 48%, transparent) 42%,
                color-mix(in srgb, var(--color-gray-950) 18%, transparent) 68%,
                transparent 100%
              )`,
            }}
          />
        ) : null}
        {isFullscreen ? (
          <div ref={playerRef} className="relative min-w-0 flex-1">
            {/* The player's menus normally portal to document.body, which native fullscreen hides. */}
            <UNSAFE_PortalProvider getContainer={() => playerRef.current}>
              <AudioPlayer
                variant="overlay"
                autoFocus
                onPopoverOpenChange={setHasOpenPlayerPopover}
              />
            </UNSAFE_PortalProvider>
          </div>
        ) : null}
        <AriaButton
          ref={fullscreenToggleRef}
          aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
          onPress={onFullscreenToggle}
          className={twMerge(
            "relative ml-auto flex size-9 shrink-0 items-center justify-center rounded-md p-0 text-white outline-none focus-visible:ring-2 focus-visible:ring-white",
            isFullscreen
              ? "bg-transparent hover:bg-white/15"
              : "bg-black/40 hover:bg-black/55",
          )}
        >
          {isFullscreen ? (
            <IconArrowsMinimize aria-hidden className="size-5" />
          ) : (
            <IconArrowsMaximize aria-hidden className="size-5" />
          )}
        </AriaButton>
      </div>
    </div>
  );
};
